import React, { useEffect, useState }  from 'react';
import DataList from '../DataList';
import ApiConfigFactory from '../../configurations/ApiConfigFactory';
import axios from 'axios';

// Define the expandable row component
const ExpandableRow = ({ row }) => {
  // Check if the row has plan_items and if it's an array
  if (!row.plan_items || !Array.isArray(row.plan_items)) {
    return <div>No plan items available.</div>;
  }
  // Render the details of the treatment plan
  return (
    <>
      <div>
        <h4>Plan</h4>
        <ul>
          {row.plan_items.map((item, index) => (
            <li key={index}>
              <strong>{item.name}</strong> {item.route?'Route:':''} {item.route} {item.Dosage?'Dosage:':''} {item.Dosage} {item.frequency?'Frequency:':''} <strong>{item.frequency}</strong>
            </li>
          ))}
        </ul>
        <p><strong>Notes:</strong> {row.notes}</p>
      </div>
    </>
  );
};

const TreatmentPlan = ({ admissionId, OnChange }) => {
  const [existingPlans, setExistingPlans] = useState([]); // State to store existing plans
  const [loading, setLoading] = useState(true); // State to track loading status

// Fetch existing plans
  const fetchPlans = async () => {
    try {
        if (!admissionId) {
        throw new Error('Admission ID is required to fetch existing plans.');
        }

        const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/treatment-plans`, {
        params: { admission_id: admissionId },
        });

        setExistingPlans(response.data || []); // Store the fetched plans in state
    } catch (error) {
        console.error('Error fetching existing plans:', error.message);
    } finally {
        setLoading(false); // Set loading to false after fetching
    }
    };

  useEffect(() => {
    fetchPlans();
  }, [admissionId]);

  const dlConfig = new ApiConfigFactory(
    'admissions/treatment-plans', // serverRoute
    'Treatment Plans',                   // title
    'admission_id',                      // parentIDName
    admissionId                          // parentIDValue
  );

  dlConfig.title = `Treatment Plans for Admission: ${admissionId}`;
  dlConfig.formTitle = 'Add/Edit Treatment Plan';
  dlConfig.allowAdd = true;
  dlConfig.path = '/admission-card/treatment-plans';
  dlConfig.useLocalCaching = false;
  dlConfig.allowExport = false;
  dlConfig.expandableRows = 'On'; // Set to 'Always' to show the expandable row for all rows
  dlConfig.expandableRowsComponent = ExpandableRow; // Use the new component here
  dlConfig.allowAdd = false;
  dlConfig.addBulkAction({ name: 'Activate', endpoint: '/hospital-portal/api/admissions/treatment-plans/active' });
  dlConfig.defaultSortFieldId = 'plan_id';
  dlConfig.defaultSortDirection = 'desc';
  dlConfig.additionalValidation = (formData) => {
    let error = {};
    console.log("starting validation.");

    if (formData.start_date && formData.end_date) {
        const startDate = new Date(formData.start_date);
        const endDate = new Date(formData.end_date);
        if (startDate > endDate) {
          error.message = 'Start date cannot be later than end date.';
        }
      }

    for (const plan of existingPlans) {
      const planStartDate = new Date(plan.start_date);
      const planEndDate = new Date(plan.end_date);
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      //only check for overlapping dates if the plan is not the same as the one being edited
      if (plan.plan_id === formData.plan_id) {
        continue;
      }
      // Check if the new plan dates overlap with existing plans
      if ((startDate >= planStartDate && startDate <= planEndDate) || (endDate >= planStartDate && endDate <= planEndDate)) {
        error.message = 'The treatment plan dates overlap with an existing treatment plan.';
        break;
      }
    }

    error.isValid = !error.message;
    return error;

  };

  dlConfig.onCreateSuccess = () => {
    fetchPlans(); // Refresh existing plans
  };
  dlConfig.onUpdateSuccess = () => {
    OnChange();
  }

  dlConfig
    .addField({ name: 'plan_id', label: 'Plan ID', type: 'number', identifier: true, disabled: true, tab: 'Plan' })
    .addField({ name: 'admission_id', label: 'Admission ID', type: 'number', defaultValue: admissionId, visible: false })
    .addField({ name: 'doctor_name', label: 'Doctor Name', type: 'text', required: true, tab: 'Plan' })
    .addField({ name: 'start_date', label: 'Start Date', type: 'datetime-local', required: true, tab: 'Plan',defaultValue: new Date().toISOString().slice(0, 16) })
    .addField({ name: 'end_date', label: 'End Date', type: 'datetime-local', required: true, tab: 'Plan' })
    .addField({ name: 'notes', label: 'Notes', type: 'textarea', tab: 'Plan', maxRows: 5 })
    .addField({
        name: 'plan_items',
        label: 'Treatment Plan',
        type: 'advanced-array',
        endpoint: '/hospital-portal/api/items/search/by-name',
        tab: 'Plan',
        showItemNotFoundWarning:false,
        additionalFields: [
          { name: 'route', label: 'Route', type: 'dropdown', options: [
            {value:'IV',label:'IV'},
            {value:'IM',label:'IM'},
            {value:'PO',label:'PO'},
            {value:'PR',label:'PR'},
            {value:'S/C',label:'S/C'},
            {value:'PV',label:'PV'}],
            width: '5%' },
          { name: 'Dosage', label: 'Dosage', type: 'text', width: '10%' },
          { name: 'frequency', label: 'Frequency', type: 'text', width: '10%'}
        ]
      })
  dlConfig
    .addTab({ name: 'Plan', roles: 'all', default: true });

  dlConfig
    .addColumn({ id: 'plan_id', name: 'Plan ID', selector: (row) => row.plan_id, type: 'number', sortable: true, identifier: true })
    .addColumn({ name: 'Doctor Name', selector: (row) => row.doctor_name, type: 'text', sortable: true })
    .addColumn({ name: 'Start Date', selector: (row) => row.start_date, type: 'datetime', sortable: true })
    .addColumn({ name: 'End Date', selector: (row) => row.end_date, type: 'datetime', sortable: true })
    .addColumn({ name: 'Notes', selector: (row) => row.notes, type: 'text' });

  return <DataList apiConfig={dlConfig} columns={dlConfig.columns} />;


};


export default TreatmentPlan;
